export interface JwtPayload {
    userId: string
    email: string
    isAdmin: boolean
    iat?: number
    exp?: number
}

/**
 * Decode a JWT token and return the payload
 * This is a simple base64 decode - it does NOT verify the token signature
 * Only use this for extracting non-sensitive information like isAdmin
 */
export const decodeJwt = (token: string): JwtPayload | null => {
    try {
        // JWT has 3 parts separated by dots: header.payload.signature
        const parts = token.split('.')
        if (parts.length !== 3) {
            console.warn('Invalid JWT format')
            return null
        }

        // Decode the payload (second part)
        const payload = parts[1]

        // Add padding if needed for base64 decode
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4)

        // Decode from base64
        const decodedPayload = atob(paddedPayload)

        // Parse JSON
        const parsedPayload = JSON.parse(decodedPayload) as JwtPayload

        return parsedPayload
    } catch (error) {
        console.error('Error decoding JWT:', error)
        return null
    }
}

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
    const payload = decodeJwt(token)
    if (!payload || !payload.exp) {
        return true
    }

    // exp is in seconds, Date.now() is in milliseconds
    return payload.exp * 1000 < Date.now()
}

/**
 * Get isAdmin from JWT token safely
 */
export const getIsAdminFromToken = (token: string | null): boolean => {
    if (!token) return false

    const payload = decodeJwt(token)
    return payload?.isAdmin || false
} 